import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { useState } from "react";

import { Reveal } from "@/components/site/Reveal";
import { business } from "@/lib/business";

type ContactProps = {
  initialStatus?: "sent" | "error" | undefined;
};

export function Contact({ initialStatus }: ContactProps = {}) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    initialStatus ?? "idle",
  );
  const [error, setError] = useState(
    initialStatus === "error" ? "We couldn't send your request. Please call the shop." : "",
  );

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setStatus("sending");
    setError("");

    try {
      const response = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: String(data.get("name") ?? ""),
          phone: String(data.get("phone") ?? ""),
          email: String(data.get("email") ?? ""),
          engine: String(data.get("engine") ?? ""),
          message: String(data.get("message") ?? ""),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        setError(payload.error ?? "We couldn't send your request. Please call the shop.");
        setStatus("error");
        return;
      }
      form.reset();
      setStatus("sent");
    } catch {
      setError("We couldn't send your request. Please call the shop.");
      setStatus("error");
    }
  }

  return (
    <section id="contact" className="scroll-mt-28 py-20 sm:py-28" aria-labelledby="contact-heading">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-2">
        <Reveal variant="left">
          <p className="eyebrow">Contact</p>
          <h2 id="contact-heading" className="mt-3 text-4xl uppercase sm:text-5xl">
            Call the shop — that's the fastest way
          </h2>
          <p className="mt-4 text-muted-foreground">
            Tell us what engine you have and what it's doing. If we're on a machine and miss your
            call, we'll call you back within 24 hours during business hours.
          </p>

          <a href={business.phoneHref} className="btn-base btn-primary mt-7 text-2xl">
            <Phone className="size-6" aria-hidden="true" />
            {business.phone}
          </a>

          <ul className="mt-10 grid gap-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="size-5 shrink-0 text-primary-text" aria-hidden="true" />
              <a href={business.mapsUrl} className="underline-offset-4 hover:underline">
                {business.address}
                <br />
                {business.city}, {business.state} {business.zip}
              </a>
            </li>
            <li className="flex gap-3">
              <Clock className="size-5 shrink-0 text-primary-text" aria-hidden="true" />
              <span>
                {business.hours}
                <br />
                {business.lunchBreak}
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="size-5 shrink-0 text-primary-text" aria-hidden="true" />
              <a href={`mailto:${business.email}`} className="underline">
                {business.email}
              </a>
            </li>
          </ul>
        </Reveal>

        <Reveal variant="right" delay={120}>
          <form
            id="request-machine-work"
            action="/api/public/contact"
            method="post"
            onSubmit={handleSubmit}
            aria-labelledby="request-heading"
            aria-describedby="contact-status"
            className="surface-card grid gap-4 p-6 sm:p-8"
          >
            <h3 id="request-heading" className="text-2xl uppercase">
              Request machine work
            </h3>
            <p className="text-sm text-muted-foreground">
              Required fields are marked. For the fastest answer, call the shop.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5 text-sm font-medium">
                Name
                <span aria-hidden="true"> *</span>
                <span className="sr-only"> (required)</span>
                <input name="name" required autoComplete="name" maxLength={100} className="field" />
              </label>
              <label className="grid gap-1.5 text-sm font-medium">
                Phone
                <span aria-hidden="true"> *</span>
                <span className="sr-only"> (required)</span>
                <input
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  required
                  autoComplete="tel"
                  maxLength={30}
                  className="field"
                />
              </label>
            </div>
            <label className="grid gap-1.5 text-sm font-medium">
              Email (optional)
              <input
                name="email"
                type="email"
                autoComplete="email"
                maxLength={255}
                className="field"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-medium">
              Engine or vehicle
              <input
                name="engine"
                autoComplete="off"
                maxLength={200}
                placeholder="e.g. 5.3L Chevy block, John Deere tractor engine"
                className="field"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-medium">
              What do you need done?
              <span aria-hidden="true"> *</span>
              <span className="sr-only"> (required)</span>
              <textarea name="message" rows={5} required maxLength={2000} className="field" />
            </label>
            <button
              type="submit"
              disabled={status === "sending"}
              className="btn-base btn-primary w-full disabled:opacity-70"
            >
              <Send className="size-4" aria-hidden="true" />
              {status === "sending" ? "Sending…" : "Send request"}
            </button>
            <p
              id="contact-status"
              className="text-xs text-muted-foreground"
              role={status === "error" ? "alert" : "status"}
              aria-live={status === "error" ? "assertive" : "polite"}
            >
              {status === "sent"
                ? "Thanks — your request was sent to the shop. We'll get back to you within 24 business hours."
                : status === "error"
                  ? error
                  : "Your request goes straight to the shop. For the fastest answer, call us."}
            </p>
          </form>
        </Reveal>
      </div>

      <div className="mx-auto mt-12 max-w-6xl px-4">
        <p className="eyebrow">Find the shop</p>
        <h3 className="mt-3 text-3xl uppercase sm:text-4xl">
          {business.address}, {business.city}, {business.state}
        </h3>
        <div
          className="mt-6 overflow-hidden rounded-lg border border-border"
          style={{ boxShadow: "var(--shadow-hard)" }}
        >
          <iframe
            title={`${business.name} on Google Maps`}
            src={business.googleMapsEmbedUrl}
            width="600"
            height="450"
            loading="lazy"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="block h-[280px] w-full border-0 sm:h-[400px] lg:h-[450px]"
          />
        </div>
        <p className="mt-3 text-sm">
          <a href={business.mapsUrl} className="underline underline-offset-4">
            Get directions to {business.address}, {business.city}
          </a>
        </p>
      </div>
    </section>
  );
}
